import React from 'react';
import { Card } from '../../ui/Card';
import { useWeight } from '../../../context/WeightContext';
import { useAuth } from '../../../context/AuthContext';
import { sb } from '../../../modules/state';

interface UnitSelectionProps {
  onComplete: () => void;
}

export function UnitSelection({ onComplete }: UnitSelectionProps) {
  const { currentUser } = useAuth();
  const { setUnitPreferences } = useWeight();
  const [preferredWeightUnit, setPreferredWeightUnit] = React.useState<'kg' | 'lbs'>('kg');
  const [preferredMeasureUnit, setPreferredMeasureUnit] = React.useState<'cm' | 'in'>('cm');
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);

  const handleSave = async () => {
    const prefs = {
      preferred_weight_unit: preferredWeightUnit,
      preferred_measure_unit: preferredMeasureUnit
    };

    if (!currentUser?.id) {
      setUnitPreferences(prefs);
      return;
    }

    setSaving(true);
    try {
      await sb.from('profiles').upsert({
        id: currentUser.id,
        preferred_weight_unit: preferredWeightUnit,
        preferred_measure_unit: preferredMeasureUnit
      });
      setUnitPreferences(prefs);
      setSaving(false);
      setSaved(true);
      setTimeout(() => {
        onComplete();
      }, 500);
    } catch (error) {
      console.error('[UnitSelection] Error saving:', error);
      setSaving(false);
    }
  };

  if (saved) {
    return (
      <div className="space-y-4 p-4">
        <div className="text-center">
          <div className="mb-4 flex h-16 w-16 mx-auto rounded-full bg-primary/20 items-center justify-center">
            <span className="text-3xl">✓</span>
          </div>
          <h2 className="text-xl font-bold text-on-surface">Unit Preferences Saved!</h2>
          <p className="text-on-surface-variant mt-2">You can now track your weight and measurements.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4">
      <div>
        <h2 className="text-xl font-bold text-on-surface">Select Your Units</h2>
        <p className="text-on-surface-variant mt-1">Choose how you want to see your weight and measurements.</p>
      </div>

      <Card className="bg-surface-container border border-outline p-4">
        <label className="block text-sm font-medium text-on-surface-variant mb-3">Weight Unit</label>
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => setPreferredWeightUnit('kg')}
            className={`flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all ${
              preferredWeightUnit === 'kg'
                ? 'border-primary bg-primary/10'
                : 'border-outline bg-surface hover:bg-surface-container-high'
            }`}
          >
            <span className="text-2xl font-bold text-primary">kg</span>
            <span className="text-xs text-on-surface-variant mt-1">Metric</span>
          </button>
          <button
            onClick={() => setPreferredWeightUnit('lbs')}
            className={`flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all ${
              preferredWeightUnit === 'lbs'
                ? 'border-primary bg-primary/10'
                : 'border-outline bg-surface hover:bg-surface-container-high'
            }`}
          >
            <span className="text-2xl font-bold text-primary">lbs</span>
            <span className="text-xs text-on-surface-variant mt-1">Imperial</span>
          </button>
        </div>
      </Card>

      <Card className="bg-surface-container border border-outline p-4">
        <label className="block text-sm font-medium text-on-surface-variant mb-3">Measurement Unit</label>
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => setPreferredMeasureUnit('cm')}
            className={`flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all ${
              preferredMeasureUnit === 'cm'
                ? 'border-primary bg-primary/10'
                : 'border-outline bg-surface hover:bg-surface-container-high'
            }`}
          >
            <span className="text-2xl font-bold text-primary">cm</span>
            <span className="text-xs text-on-surface-variant mt-1">Centimeters</span>
          </button>
          <button
            onClick={() => setPreferredMeasureUnit('in')}
            className={`flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all ${
              preferredMeasureUnit === 'in'
                ? 'border-primary bg-primary/10'
                : 'border-outline bg-surface hover:bg-surface-container-high'
            }`}
          >
            <span className="text-2xl font-bold text-primary">in</span>
            <span className="text-xs text-on-surface-variant mt-1">Inches</span>
          </button>
        </div>
      </Card>

      <div className="fixed bottom-0 left-0 right-0 p-4 bg-surface border-t border-outline">
        <button
          className="w-full rounded-xl bg-primary px-4 py-3.5 font-bold text-primary-contrast shadow-lg shadow-primary/30 active:scale-95 transition-transform"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Complete Setup'}
        </button>
      </div>
    </div>
  );
}
